import {createSlice} from '@reduxjs/toolkit';
import {
    createSavedSetting,
    deleteSavedSetting,
    fetchPresets,
    fetchSavedSettingById,
    fetchSavedSettingsByUser,
    updateSavedSetting,
} from './savedSettingsActions';

const initialState = {
    items: [],           // list of all saved settings for current user
    presets: [],
    current: null,       // single setting (for edit/view)
    isLoading: false,
    selectedSettingId: '', // New field to track the selected setting

    error: null
};

const savedSettingsSlice = createSlice({
    name: 'savedSettings',
    initialState,
    reducers: {
        clearCurrentSetting: (state) => {
            state.current = null;
            state.error = null;
        },
        // Add new reducer to set the selected setting ID
        setSelectedSettingId: (state, action) => {
            state.selectedSettingId = action.payload;
        }
    },
    extraReducers: (builder) => {
        // -- Fetch all for user --
        builder.addCase(fetchSavedSettingsByUser.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(fetchSavedSettingsByUser.fulfilled, (state, action) => {
            state.isLoading = false;
            state.items = action.payload;
        });
        builder.addCase(fetchSavedSettingsByUser.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // -- Fetch single by ID --
        builder.addCase(fetchSavedSettingById.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(fetchSavedSettingById.fulfilled, (state, action) => {
            state.isLoading = false;
            state.current = action.payload;
        });
        builder.addCase(fetchSavedSettingById.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // -- Create new --
        builder.addCase(createSavedSetting.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(createSavedSetting.fulfilled, (state, action) => {
            state.isLoading = false;
            state.items.push(action.payload);
        });
        builder.addCase(createSavedSetting.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // -- Update existing --
        builder.addCase(updateSavedSetting.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(updateSavedSetting.fulfilled, (state, action) => {
            state.isLoading = false;
            const idx = state.items.findIndex(s => s.savedSettingsId === action.payload.savedSettingsId);
            if (idx !== -1) state.items[idx] = action.payload;
            if (state.current?.savedSettingsId === action.payload.savedSettingsId) {
                state.current = action.payload;
            }
        });
        builder.addCase(updateSavedSetting.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // -- Delete --
        builder.addCase(deleteSavedSetting.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(deleteSavedSetting.fulfilled, (state, action) => {
            state.isLoading = false;
            state.items = state.items.filter(s => s.savedSettingsId !== action.payload);
            if (state.current?.savedSettingsId === action.payload) {
                state.current = null;
            }
            // Clear the selectedSettingId if the deleted setting was selected
            if (state.selectedSettingId === String(action.payload)) {
                state.selectedSettingId = '';
            }
        });
        builder.addCase(deleteSavedSetting.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // -- Fetch all presets --
        builder.addCase(fetchPresets.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(fetchPresets.fulfilled, (state, action) => {
            state.isLoading = false;
            state.presets = action.payload;
        });
        builder.addCase(fetchPresets.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });
    }
});

// Export actions
export const {clearCurrentSetting, setSelectedSettingId} = savedSettingsSlice.actions;

// Selectors
export const selectSavedSettings = (state) => state.savedSettings.items;
export const selectPresetSettings = state => state.savedSettings.presets;

export const selectCurrentSetting = (state) => state.savedSettings.current;
export const selectSavedSettingsLoading = (state) => state.savedSettings.isLoading;
export const selectSavedSettingsError = (state) => state.savedSettings.error;

export default savedSettingsSlice.reducer;
